import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Check } from "lucide-react";

const LIENS = {
  second_mortgage: "Second mortgage", heloc: "HELOC", pace: "PACE assessment", solar: "Solar financing", hoa: "HOA/COA lien",
  judgment: "Judgment lien", irs: "IRS lien", state_tax: "State tax lien", child_support: "Child-support lien",
  code_enforcement: "Code-enforcement lien", municipal: "Municipal lien", mechanic: "Contractor/mechanic lien",
  private_loan: "Private loan", reverse_mortgage: "Reverse mortgage", other: "Other recorded claim",
};

const Choice = ({ selected, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`tap flex min-h-[52px] w-full items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 text-left text-base font-medium ${selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
    <span>{children}</span>
    {selected && <Check className="h-5 w-5 shrink-0 text-amber-300" />}
  </button>
);

const ConfChips = ({ conf, setConf }) => (
  <div className="mt-3 flex flex-wrap gap-2">
    {[["reported", "Exact figure"], ["estimated", "Rough estimate"], ["unknown", "I don't know"]].map(([v, l]) => (
      <button key={v} type="button" data-testid={`confidence-chip-${v}`} onClick={() => setConf(v)}
        className={`tap rounded-full border px-3 py-1.5 text-sm font-semibold ${conf === v ? "border-amber-600 bg-amber-100 text-amber-900" : "border-slate-300 bg-white text-slate-600"}`}>{l}</button>
    ))}
  </div>
);

const LienRow = ({ k, label, row, set }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`lien-row-${k}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-semibold">{label}</span>
      <div className="flex gap-1">
        {["yes", "no", "unknown"].map((v) => (
          <button key={v} type="button" data-testid={`lien-${k}-${v}`} onClick={() => set({ ...row, has: v })}
            className={`tap rounded-full px-3 py-1 text-sm font-semibold ${row.has === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{v === "unknown" ? "Not sure" : v[0].toUpperCase() + v.slice(1)}</button>
        ))}
      </div>
    </div>
    {row.has === "yes" && (
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {[["creditor", "Creditor"], ["balance", "Approx. balance ($)"], ["payment", "Monthly payment ($)"], ["status", "Status (current/behind)"], ["lien_status", "Recorded lien? (yes/no/unknown)"], ["docs", "Documents available? (yes/no)"]].map(([f, l]) => (
          <Input key={f} data-testid={`lien-${k}-${f}`} placeholder={l} value={row[f] || ""} inputMode={["balance", "payment"].includes(f) ? "decimal" : "text"}
            onChange={(e) => set({ ...row, [f]: e.target.value })} />
        ))}
      </div>
    )}
  </div>
);

export const QuestionInput = ({ q, initial, onChange }) => {
  const [val, setVal] = useState(initial?.value ?? (q.type === "multi" ? [] : ["group", "liens", "address"].includes(q.type) ? {} : ""));
  const [conf, setConf] = useState(initial?.confidence || "reported");
  useEffect(() => { onChange(val, conf); }, [val, conf]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.type === "single") {
    return <div className="grid gap-2">{q.options.map((o) => <Choice key={o.v} testId={`option-${q.key}-${o.v}`} selected={val === o.v} onClick={() => setVal(o.v)}>{o.l}</Choice>)}</div>;
  }
  if (q.type === "multi") {
    const toggle = (v) => {
      if (v === "none") return setVal(val.includes("none") ? [] : ["none"]);
      setVal(val.includes(v) ? val.filter((x) => x !== v) : [...val.filter((x) => x !== "none"), v]);
    };
    return <div className="grid gap-2 sm:grid-cols-2">{q.options.map((o) => <Choice key={o.v} testId={`option-${q.key}-${o.v}`} selected={val.includes(o.v)} onClick={() => toggle(o.v)}>{o.l}</Choice>)}</div>;
  }
  if (q.type === "address") {
    return (
      <div className="grid grid-cols-2 gap-2">
        {[["street", "Street address", 2], ["city", "City", 1], ["county", "County", 1], ["state", "State", 1], ["zip", "ZIP code", 1]].map(([f, l, span]) => (
          <Input key={f} data-testid={`address-${f}`} className={`h-12 text-base ${span === 2 ? "col-span-2" : ""}`} placeholder={l} value={val[f] || ""} onChange={(e) => setVal({ ...val, [f]: e.target.value })} />
        ))}
      </div>
    );
  }
  if (q.type === "group") {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {q.fields.map(([f, l]) => (
          <label key={f} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <span className="text-sm font-medium text-slate-600">{l}</span>
            <Input data-testid={`group-${q.key}-${f}`} inputMode="decimal" className="h-10 w-32 text-right" placeholder="$0" value={val[f] ?? ""} onChange={(e) => setVal({ ...val, [f]: e.target.value.replace(/[^0-9.]/g, "") })} />
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "liens") {
    return <div className="grid gap-2">{Object.entries(LIENS).map(([k, l]) => <LienRow key={k} k={k} label={l} row={val[k] || {}} set={(r) => setVal({ ...val, [k]: r })} />)}</div>;
  }
  if (q.type === "money" || q.type === "number") {
    return (
      <div>
        <div className="relative">
          {q.type === "money" && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-slate-400">$</span>}
          <Input data-testid={`input-${q.key}`} inputMode="decimal" disabled={conf === "unknown"} className={`h-14 text-xl ${q.type === "money" ? "pl-9" : ""}`}
            value={conf === "unknown" ? "" : val ?? ""} onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={q.suffix ? `0${q.suffix}` : "0"} />
        </div>
        {q.type === "money" && <ConfChips conf={conf} setConf={setConf} />}
      </div>
    );
  }
  if (q.type === "date") {
    return <Input data-testid={`input-${q.key}`} type="date" className="h-14 text-lg" value={val || ""} onChange={(e) => setVal(e.target.value)} />;
  }
  return <Input data-testid={`input-${q.key}`} className="h-14 text-lg" placeholder={q.placeholder} value={val || ""} onChange={(e) => setVal(e.target.value)} />;
};
